import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Calendar, Users, ArrowLeftRight } from "lucide-react";
import airportGroupsUS from "../airportsGroupsUs";
import airports from "../airports";

const FlightSearchForm = () => {
  const navigate = useNavigate();
  const travelerRef = useRef(null);

  const [formData, setFormData] = useState({
    tripType: "roundtrip",
    from: "",
    to: "",
    departureDate: "",
    returnDate: "",
  });

  const [displayFrom, setDisplayFrom] = useState("");
  const [displayTo, setDisplayTo] = useState("");

  const [travelers, setTravelers] = useState({
    adults: 1,
    children: 0,
    infants: 0,
    cabin: "Economy",
  });

  const [filteredAirports, setFilteredAirports] = useState({ from: [], to: [] });
  const [showAirportDropdown, setShowAirportDropdown] = useState({ from: false, to: false });
  const [showTravelerBox, setShowTravelerBox] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (travelerRef.current && !travelerRef.current.contains(e.target)) {
        setShowTravelerBox(false);
      }
      if (!e.target.closest("#from")) setShowAirportDropdown((p) => ({ ...p, from: false }));
      if (!e.target.closest("#to")) setShowAirportDropdown((p) => ({ ...p, to: false }));
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "from") setDisplayFrom(value);
    if (name === "to") setDisplayTo(value);

    if (name === "from" || name === "to") {
      if (!value) {
        setShowAirportDropdown((p) => ({ ...p, [name]: false }));
        return;
      }
      const searchableAirports = [
        ...airportGroupsUS.map((g) => ({
          type: "group",
          code: g.code,
          name: `${g.name} (All Airports)`,
          airports: g.airports,
        })),
        ...airports.map((a) => ({ type: "airport", code: a.code, name: a.name })),
      ];
      const matches = searchableAirports
        .filter(
          (a) =>
            a.name.toLowerCase().includes(value.toLowerCase()) ||
            a.code.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 10);
      setFilteredAirports((p) => ({ ...p, [name]: matches }));
      setShowAirportDropdown((p) => ({ ...p, [name]: true }));
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }
  };

  const selectAirport = (name, item) => {
    setFormData((p) => ({ ...p, [name]: item.code }));
    const label = `${item.name} (${item.code})`;
    if (name === "from") setDisplayFrom(label);
    if (name === "to") setDisplayTo(label);
    setShowAirportDropdown((p) => ({ ...p, [name]: false }));
  };

  const swapAirports = () => {
    setFormData((p) => ({ ...p, from: p.to, to: p.from }));
    const tmp = displayFrom;
    setDisplayFrom(displayTo);
    setDisplayTo(tmp);
  };

  const updateTravelerCount = (type, op) => {
    setTravelers((p) => {
      const value = Math.max(0, p[type] + (op === "inc" ? 1 : -1));
      return { ...p, [type]: type === "adults" ? Math.max(1, value) : value };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    navigate("/results", { state: { ...formData, travelers } });
  };

  const totalTravelers = travelers.adults + travelers.children + travelers.infants;

  return (
    <section
      className="relative sky-grid overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #060d1f 0%, #0f1e3d 50%, #1a3260 100%)",
        minHeight: "420px",
      }}
    >
      {/* Ambient blobs */}
      <div
        className="absolute top-0 left-1/4 w-96 h-96 rounded-full pointer-events-none opacity-20"
        style={{ background: "radial-gradient(circle, #38bdf8 0%, transparent 70%)", filter: "blur(60px)" }}
      />
      <div
        className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full pointer-events-none opacity-15"
        style={{ background: "radial-gradient(circle, #7c3aed 0%, transparent 70%)", filter: "blur(60px)" }}
      />

      <div className="relative max-w-7xl mx-auto px-4 py-12 md:py-16">
        {/* Hero headline */}
        <div className="mb-8 anim-fade-up">
          <h1
            className="text-3xl md:text-5xl text-white mb-3 leading-tight"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            Your Gateway to <em className="text-sky-400 not-italic">Smarter</em> Flights
          </h1>
          <p className="text-sky-200/80 text-base md:text-lg max-w-xl">
            Compare fares, book with confidence, and let our travel experts handle the rest.
          </p>
        </div>

        {/* Search Card */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-2xl overflow-visible anim-fade-up anim-fade-up-2"
        >
          {/* Trip type tabs */}
          <div className="flex gap-1 p-3 pb-0 border-b border-slate-100">
            {[
              { value: "roundtrip", label: "Round Trip" },
              { value: "oneway", label: "One Way" },
            ].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setFormData((p) => ({ ...p, tripType: value }))}
                className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition-all duration-200 border-b-2 ${
                  formData.tripType === value
                    ? "border-sky-500 text-sky-600 bg-sky-50"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Fields row */}
          <div className="p-4 flex flex-col md:flex-row gap-3 items-end flex-wrap">
            {/* FROM */}
            <Field label="From" icon={<MapPin size={14} className="text-sky-500" />}>
              <input
                id="from"
                name="from"
                value={displayFrom}
                onChange={handleChange}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                placeholder="City or airport"
                className={inputClass}
              />
              {showAirportDropdown.from && (
                <Dropdown>
                  {filteredAirports.from.map((a) => (
                    <DropdownItem key={a.code} onMouseDown={() => selectAirport("from", a)}>
                      <span className="font-bold text-sky-600 mr-2 text-xs">{a.code}</span>
                      {a.name}
                    </DropdownItem>
                  ))}
                </Dropdown>
              )}
            </Field>

            {/* Swap button */}
            <button
              type="button"
              onClick={swapAirports}
              className="hidden md:flex w-8 h-8 mb-0.5 shrink-0 items-center justify-center rounded-full border-2 border-slate-200 bg-white hover:border-sky-400 hover:text-sky-500 transition-all duration-200 text-slate-400 self-end"
              title="Swap airports"
            >
              <ArrowLeftRight size={14} />
            </button>

            {/* TO */}
            <Field label="To" icon={<MapPin size={14} className="text-sky-500" />}>
              <input
                id="to"
                name="to"
                value={displayTo}
                onChange={handleChange}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                placeholder="City or airport"
                className={inputClass}
              />
              {showAirportDropdown.to && (
                <Dropdown>
                  {filteredAirports.to.map((a) => (
                    <DropdownItem key={a.code} onMouseDown={() => selectAirport("to", a)}>
                      <span className="font-bold text-sky-600 mr-2 text-xs">{a.code}</span>
                      {a.name}
                    </DropdownItem>
                  ))}
                </Dropdown>
              )}
            </Field>

            {/* DEPART */}
            <Field label="Depart" icon={<Calendar size={14} className="text-sky-500" />} small>
              <input
                type="date"
                name="departureDate"
                value={formData.departureDate}
                onChange={handleChange}
                className={`${inputClass} appearance-none`}
              />
            </Field>

            {/* RETURN */}
            {formData.tripType === "roundtrip" && (
              <Field label="Return" icon={<Calendar size={14} className="text-sky-500" />} small>
                <input
                  type="date"
                  name="returnDate"
                  value={formData.returnDate}
                  onChange={handleChange}
                  min={formData.departureDate}
                  disabled={!formData.departureDate}
                  className={`${inputClass} appearance-none disabled:opacity-50`}
                />
              </Field>
            )}

            {/* PASSENGERS */}
            <Field
              label="Passengers & Cabin"
              icon={<Users size={14} className="text-sky-500" />}
              wide
              refProp={travelerRef}
            >
              <button
                type="button"
                onClick={() => setShowTravelerBox((p) => !p)}
                className={`${inputClass} text-left truncate`}
              >
                {totalTravelers} Passenger{totalTravelers > 1 ? "s" : ""},{" "}
                {travelers.cabin}
              </button>

              {showTravelerBox && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-2xl p-5 w-68 z-50 min-w-[260px]">
                  {[
                    { key: "adults", label: "Adults", sub: "Age 12+" },
                    { key: "children", label: "Children", sub: "Age 2–11" },
                    { key: "infants", label: "Infants", sub: "Under 2" },
                  ].map(({ key, label, sub }) => (
                    <div key={key} className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{label}</p>
                        <p className="text-xs text-slate-400">{sub}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <CountBtn onClick={() => updateTravelerCount(key, "dec")}>−</CountBtn>
                        <span className="text-sm font-bold w-4 text-center">{travelers[key]}</span>
                        <CountBtn onClick={() => updateTravelerCount(key, "inc")}>+</CountBtn>
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-slate-100 pt-3">
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Cabin Class</label>
                    <select
                      value={travelers.cabin}
                      onChange={(e) => setTravelers((p) => ({ ...p, cabin: e.target.value }))}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                    >
                      <option>Economy</option>
                      <option>Premium Economy</option>
                      <option>Business</option>
                      <option>First</option>
                    </select>
                  </div>
                </div>
              )}
            </Field>

            {/* SEARCH */}
            <button
              type="submit"
              className="flex items-center justify-center gap-2 h-[46px] px-7 bg-sky-600 hover:bg-sky-700 active:scale-95 text-white rounded-xl font-bold text-sm transition-all duration-200 shadow-lg hover:shadow-sky-200 w-full md:w-auto shrink-0"
            >
              <Search size={16} />
              Search Flights
            </button>
          </div>
        </form>

        {/* Trust badges */}
        {/* <div className="flex flex-wrap gap-4 mt-5 anim-fade-up anim-fade-up-3">
          {["Price Match Guarantee", "No Hidden Fees", "24/7 Expert Support", "Instant Confirmation"].map((b) => (
            <span
              key={b}
              className="text-xs font-medium px-3 py-1.5 rounded-full text-sky-100"
              style={{ background: "rgba(56,189,248,0.12)", border: "1px solid rgba(56,189,248,0.2)" }}
            >
              ✓ {b}
            </span>
          ))}
        </div> */}
      </div>
    </section>
  );
};

/* ── Helpers ── */
const inputClass =
  "w-full h-[46px] border border-slate-200 rounded-xl px-3 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition placeholder:text-slate-400";

const Field = ({ label, children, small, wide, refProp, icon }) => (
  <div
    ref={refProp}
    className={`relative flex flex-col w-full ${
      small ? "md:w-[148px]" : wide ? "md:w-[240px]" : "md:w-[210px]"
    }`}
  >
    <label className="flex items-center gap-1 text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
      {icon}
      {label}
    </label>
    {children}
  </div>
);

const Dropdown = ({ children }) => (
  <ul className="absolute top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-2xl max-h-56 overflow-auto z-50">
    {children}
  </ul>
);

const DropdownItem = ({ children, ...props }) => (
  <li
    {...props}
    className="px-4 py-2.5 text-sm text-slate-700 hover:bg-sky-50 cursor-pointer flex items-center border-b border-slate-50 last:border-0"
  >
    {children}
  </li>
);

const CountBtn = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-8 h-8 rounded-full border-2 border-slate-200 font-bold text-base flex items-center justify-center hover:border-sky-400 hover:text-sky-600 transition-all duration-150 leading-none"
  >
    {children}
  </button>
);

export default FlightSearchForm;
